/* eslint-disable @typescript-eslint/no-non-null-assertion -- Text parsing code needs to assume a specific format */
import { postResolveSecurities } from '@blk/aw-api-clients';
import { CurrencyConfiguration } from '@blk/aw-bff-typescript-models';
import * as ApiTypes from '@blk/awt-aladdinportfoliobuilderserver';
import { Security } from '@blk/awt-aladdinportfoliobuilderserver';
import DOMPurify from 'dompurify';
import {
  camelCase,
  every,
  filter,
  flatten,
  forEach,
  get,
  hasIn,
  includes,
  isEmpty,
  map,
  mapValues,
  sumBy,
  uniq,
  values,
  zipObject,
} from 'lodash';
import * as Papa from 'papaparse';
import { AnyObject } from './GeneralUtils';
import { matchHeader } from './HeaderMatcher';
import type { DecimalSeparatorFormat } from './NumberUtils';
import { parseLocaleStringToNumber } from './NumberUtils';

type ParsedData = {
  headers: string[];
  data: object;
};

interface SecuritiesStore {
  [cusip: string]: Security;
}

export interface PosInfo extends AnyObject {}

export type TPosition = {
  identifier?: string;
  identifierType?: string;
  cusip?: string;
  weight?: number;
  marketValue?: number;
  quantity?: number;
  notionalMarketValue?: number;
  identifierTypeImplied?: boolean;
  currency?: string;
  market?: string;
  costBasis?: number;
  purchaseDate?: string;
  portfolioID?: string;
  posInfo?: PosInfo;
};

type UniqueIdentifier = {
  identifier: string;
  identifierType: string;
  identifierTypeImplied: boolean;
  cusip?: string;
  currency?: string;
  market?: string;
};

export enum FailedResolutionErrorTypes {
  MULTIPLE_CURRENCIES = 'MULTIPLE_CURRENCIES',
  MULTIPLE_SECURITY_MAPPINGS = 'MULTIPLE_SECURITY_MAPPINGS',
  UNRECOGNIZED_POSITIONS = 'UNRECOGNIZED_POSITIONS',
}

export type FailedResolution = UniqueIdentifier & {
  message: string;
  alternates: Security[];
  shouldSkipCurrencySelection?: boolean;
  error: FailedResolutionErrorTypes;
  selectedSecurity?: SelectedSecurity;
};

export type SelectedSecurity = {
  security: Security;
  isReplaced: boolean;
  isSelected: boolean;
};

export type SecurityMap = { [cusip: string]: Security };
export type FileHeaderMappings = { [header: string]: string[] };

export interface ImportedPortfolio {
  failedResolutions: FailedResolution[];
  marketValue: number;
  positions: TPosition[];
  securityMap: SecurityMap;
  totalWeight: number;
}

const securityIdentifierHierarchy = ['cusip', 'clientId', 'ticker', 'sedol', 'isin'];

export const SECURITY_BATCH_THRESHOLDS = {
  SMALL: { maxItems: 100, batchSize: 50 },
  MEDIUM: { maxItems: 2000, batchSize: 1000 },
  LARGE: { batchSize: 5500 },
} as const;

function getBatchSize(totalItems: number): number {
  if (totalItems <= SECURITY_BATCH_THRESHOLDS.SMALL.maxItems) {
    return SECURITY_BATCH_THRESHOLDS.SMALL.batchSize;
  }
  if (totalItems <= SECURITY_BATCH_THRESHOLDS.MEDIUM.maxItems) {
    return SECURITY_BATCH_THRESHOLDS.MEDIUM.batchSize;
  }
  return SECURITY_BATCH_THRESHOLDS.LARGE.batchSize;
}

function isPositionKey(key: string, position: TPosition): key is keyof TPosition {
  return !isEmpty(position[key as keyof TPosition]);
}

function getPositionIdentifier(position: TPosition): TPosition | undefined {
  /* Case when file has identifierType and indentifier columns. */
  const identifierType = !isEmpty(position.identifierType) ? position.identifierType?.trim() : null;
  const identifier = !isEmpty(position.identifier) ? position.identifier?.trim() : null;
  if (identifierType !== null && identifier !== null) {
    return { ...position, identifier, identifierType };
  }
  /* Case when file has identifierType as a column, example, a column of cusips */
  for (const idField of securityIdentifierHierarchy) {
    if (isPositionKey(idField, position)) {
      return { ...position, identifier: position[idField] as string, identifierType: idField };
    }
  }
  /* Case when we have an identifier column of mixed identifiers */
  if (identifier !== null) {
    if (identifier?.length === 12) {
      return { ...position, identifier, identifierType: 'isin', identifierTypeImplied: true };
    }
    if (identifier?.length === 9) {
      return { ...position, identifier, identifierType: 'cusip', identifierTypeImplied: true };
    }
    if (identifier?.length === 7) {
      return { ...position, identifier, identifierType: 'sedol', identifierTypeImplied: true };
    }
    /*
     * If it is non of the above, we make a reasonable guess that it could be a ticker. If it is not a ticker, we
     *  catch it below with appropriate error handling.
     */
    return { ...position, identifier, identifierType: 'ticker', identifierTypeImplied: true };
  }
  /* If user give us a invalid format */
  return undefined;
}

function cleanIdentifier(positionIdentifier: TPosition) {
  positionIdentifier.identifierType = camelCase(positionIdentifier.identifierType);
  positionIdentifier.identifier = positionIdentifier.identifier?.trim();
  // Make ticker and cusip case agnostic;
  if (positionIdentifier.identifierType === 'ticker' || positionIdentifier.identifierType === 'cusip') {
    positionIdentifier.identifier = positionIdentifier.identifier?.toUpperCase();
  }
  // Handle clientid edge case;
  if (positionIdentifier.identifierType === 'clientid') {
    positionIdentifier.identifierType = 'clientId';
  }
  return positionIdentifier;
}

function getPosIdentifierKey(pos: TPosition) {
  const { identifier, identifierType, currency = '', market = '' } = pos;
  return `${identifierType}${identifier}${currency}${market}`;
}

interface BuildPositionsFromRowsParams {
  currencies: CurrencyConfiguration[];
  date: string;
  decimalSeparatorFormat?: DecimalSeparatorFormat;
  defaultPortfolioCurrency: string;
  enableDefaultPortfolioCurrency: boolean;
  enableMatchRegionToMarket?: boolean;
  filters: ApiTypes.FilterDefinition[] | undefined;
  isIndexOnlyUpload?: boolean;
  parsedData: ParsedData;
  pkg: string;
  primarySecurityIdentifier?: string;
  securitiesStore: SecuritiesStore;
}

export async function buildPositionsFromRows({
  parsedData,
  currencies,
  securitiesStore,
  date,
  pkg,
  enableDefaultPortfolioCurrency,
  defaultPortfolioCurrency,
  filters,
  enableMatchRegionToMarket,
  isIndexOnlyUpload,
  primarySecurityIdentifier,
  decimalSeparatorFormat,
}: BuildPositionsFromRowsParams): Promise<ImportedPortfolio> {
  let marketValue = 0;
  const failedResolutions: FailedResolution[] = [];

  // convert parsedData to positions for easier processing
  let parsedPositions: TPosition[] = map(parsedData.data, (row) =>
    mapValues(
      // match up data with headers
      zipObject(parsedData.headers, row),
      // convert numeric values into numbers
      (value, key) =>
        (key === 'marketValue' ||
          key === 'weight' ||
          key === 'notionalMarketValue' ||
          key === 'quantity' ||
          key === 'costBasis') &&
        typeof value === 'string'
          ? parseLocaleStringToNumber(value, decimalSeparatorFormat) || 0
          : value
    )
  );
  if (includes(parsedData.headers, 'marketValue')) {
    const derivedData = deriveWeightsFromMarketValue(parsedPositions);
    if (!derivedData) {
      return Promise.reject(new Error('No derived data'));
    }
    parsedPositions = derivedData.positions;
    marketValue = derivedData.marketValue;
  }
  // enforce column order
  let totalWeight = 0;
  let uniqueIdentifiers: { [key: string]: UniqueIdentifier } = {};
  let identifiedPositions: TPosition[] = [];

  forEach(parsedPositions, (position) => {
    let positionIdentifier = getPositionIdentifier(position);
    if (positionIdentifier) {
      positionIdentifier = cleanIdentifier(positionIdentifier);
    }

    if (
      !position.weight &&
      !position.quantity &&
      (isEmpty(positionIdentifier) || isEmpty(positionIdentifier?.identifier))
    ) {
      return;
    }

    if (!hasIn(positionIdentifier, 'identifier')) {
      return;
    }

    if (positionIdentifier) {
      const {
        identifier,
        identifierType,
        identifierTypeImplied = false,
        currency = '',
        market = '',
      } = positionIdentifier;
      const uniqueKey = getPosIdentifierKey(positionIdentifier);

      if (!uniqueIdentifiers.hasOwnProperty(uniqueKey) && identifier && identifierType) {
        uniqueIdentifiers[uniqueKey] = {
          identifier,
          identifierType,
          identifierTypeImplied,
          currency,
          market,
        };
      }
      // store imported from file dynamic custom position to posInfo
      const posInfo: PosInfo = (Object.keys(position) as Array<keyof TPosition>)
        .filter((key) => key.startsWith('custom.'))
        .reduce((obj: PosInfo, key) => {
          obj[key.replace('custom.', '')] = position[key];
          return obj;
        }, {});

      identifiedPositions.push({
        identifier,
        identifierType,
        weight: position.weight,
        marketValue: position.marketValue,
        quantity: position.quantity,
        costBasis: position.costBasis,
        purchaseDate: position.purchaseDate,
        portfolioID: position.portfolioID,
        currency,
        market,
        posInfo,
      });
      totalWeight += position.weight ?? 0;
    }
  });
  if (isEmpty(uniqueIdentifiers)) {
    throw new Error('Missing position identifier(s).  Review Quick Import instructions for expected formats.');
  }

  const securityIdentifiers = flatten(
    map(uniqueIdentifiers, ({ identifier, identifierType, identifierTypeImplied }) => {
      // If primarySecurityIdentifier is explicitly provided, only use that identifier type
      if (primarySecurityIdentifier) {
        return [{ idValue: identifier, idType: primarySecurityIdentifier }];
      }
      let identifersToReturn = [{ idValue: identifier, idType: identifierType }];
      if (identifierTypeImplied && identifierType !== 'ticker') {
        identifersToReturn.push({ idValue: identifier, idType: 'ticker' });
      }
      if (identifierTypeImplied && identifierType !== 'displayId') {
        identifersToReturn.push({ idValue: identifier, idType: 'displayId' });
      }
      if (identifierTypeImplied && identifierType !== 'clientId') {
        identifersToReturn.push({ idValue: identifier, idType: 'clientId' });
      }
      if (identifierType === 'cusip') {
        identifersToReturn.push({
          idValue: identifier,
          idType: 'publicCusip',
        });
      }
      return identifersToReturn;
    })
  );
  const batchSize = getBatchSize(securityIdentifiers.length);
  const securityMap: SecurityMap = {};

  const batches: Array<{ idValue: string; idType: string }[]> = [];
  for (let i = 0; i < securityIdentifiers.length; i += batchSize) {
    batches.push(securityIdentifiers.slice(i, i + batchSize));
  }

  const batchPromises = batches.map((batch) =>
    postResolveSecurities(batch, date, pkg)
      .then((response) => {
        const results = response.data;
        if (!results) {
          return [];
        }
        return results.filter((security) => {
          if (isIndexOnlyUpload) {
            return security.assetType === 'Index';
          } else if (filters) {
            return filters.every((filter) => doesSecurityPassFilter(security, filter));
          }
          return true;
        });
      })
      .catch((error) => {
        console.error(error);
        return [];
      })
  );

  const batchResults = await Promise.allSettled(batchPromises);

  batchResults.forEach((result) => {
    if (result.status === 'fulfilled') {
      result.value.forEach((security) => {
        securityMap[security.cusip] = security;
      });
    }
  });

  const resolvedIdentifiers: Security[] = values(securityMap);
  forEach(uniqueIdentifiers, (ui) => {
    let matches = filter(resolvedIdentifiers, (security) => {
      // Check the security object at the top level, and under .identifiers to check for a match
      let resolvedSecurityIdentifierValue =
        get(security, ui.identifierType) || get(security, `identifiers.${ui.identifierType}`);
      /**
       * AWM-21513: For security imports that are not publicly searchable, these do not have isin, sedol, ticker values
       * but its displayId is of type client.client_isin. This enables a direct match to flow through to the import
       */
      if (
        !resolvedSecurityIdentifierValue &&
        ui.identifierType === 'isin' &&
        security.displayIdType === 'client.client_isin'
      ) {
        resolvedSecurityIdentifierValue = security.displayId;
      }
      let hasDirectMatch = resolvedSecurityIdentifierValue === ui.identifier;
      if (!hasDirectMatch && ui.identifierType === 'cusip') {
        hasDirectMatch = security.publicCusip === ui.identifier;
      }
      if (!hasDirectMatch && ui.identifierTypeImplied) {
        // if ticker does not match, the least fallback to displayId to match
        hasDirectMatch = security.ticker === ui.identifier || security.displayId === ui.identifier;
      }
      if (!hasDirectMatch && ui.identifierTypeImplied && security.client) {
        hasDirectMatch = security.client.clientId === ui.identifier;
      }
      // Once a match on identifiers is found, if the user has specified required currency/market filters, then we must eliminate matches that do not match those filters
      if (hasDirectMatch && ui.currency) {
        hasDirectMatch = ui.currency === security.currency;
      }
      if (hasDirectMatch && ui.market) {
        hasDirectMatch = ui.market === security.market;
      }

      /**
       * if hasDirectMatch has been overwritten to return false, even though the resolvedSecurityIdentifierValue = ui.identifier,
       * and if enableDefaultPortfolioCurrency is configured this allows a match to flow through and enables the user to select a currency
       */
      if (resolvedSecurityIdentifierValue === ui.identifier && enableDefaultPortfolioCurrency) {
        return resolvedSecurityIdentifierValue === ui.identifier;
      }
      return hasDirectMatch;
    });
    let currencyOptions = uniq(matches.map((security) => security.currency));

    if (enableDefaultPortfolioCurrency) {
      // user included currency column
      if (ui.currency) {
        if (matches.length > 1) {
          /**
           * only filter list of matches based on user input currency if it is one of the currency options the security maps to
           * otherwise, choose defaultPortfolioCurrency if it is an option or reset user input currency and force the user to select a currency
           */
          if (currencyOptions.includes(ui.currency)) {
            matches = matches.filter((security) => security.currency === ui.currency);
          } else {
            ui.currency = currencyOptions.includes(defaultPortfolioCurrency) ? defaultPortfolioCurrency : '';
          }
        } else if (matches.length === 1) {
          // when resolved security currency doesn't match user input currency, force user to select a currency
          if (ui.currency !== matches[0].currency) {
            ui.currency = matches[0].currency;
            failedResolutions.push({
              ...ui,
              message: `${ui.identifier} could be more than one security`,
              alternates: matches,
              error: FailedResolutionErrorTypes.MULTIPLE_CURRENCIES,
            });
          }
        }
      } else {
        if (currencyOptions.includes(defaultPortfolioCurrency)) {
          matches = matches.filter((security) => security.currency === defaultPortfolioCurrency);
          // overwrite currencyOptions here since user wants to default to defaultPortfolioCurrency
          currencyOptions = [defaultPortfolioCurrency];
        }
      }
    }

    if (enableMatchRegionToMarket) {
      const securityWithMatchingCountryAndMarket = matches.find((match) => match.country === match.market);
      if (securityWithMatchingCountryAndMarket?.cusip) {
        matches = [securityWithMatchingCountryAndMarket];
      }
    }
    const cashRegex = /cash/i;
    const currenciesInSecurityStore = currencies.filter(({ cashCUSIP }) => securitiesStore[cashCUSIP]);
    const security = ui?.identifier ? securityMap[ui.identifier] : undefined;

    // If the text 'cash' is present in the identifier, ask the user if they want the cash equity security or cash monetary security
    if (cashRegex.exec(ui.identifier) !== null && currenciesInSecurityStore.length > 0) {
      const currencyMatch = currenciesInSecurityStore.find(({ currency }) => {
        const currencyRegex = new RegExp(currency, 'i');
        return currencyRegex.exec(ui.identifier) !== null;
      });
      if (currencyMatch) {
        const { currency, cashCUSIP } = currencyMatch;
        ui.currency = currency;
        if (matches.length === 0) {
          matches.push(securitiesStore[cashCUSIP]);
        }
        securityMap[cashCUSIP] = securitiesStore[cashCUSIP];
      } else {
        failedResolutions.push({
          ...ui,
          message: `${ui.identifier} could be more than one security or cash`,
          alternates: [...matches, ...currenciesInSecurityStore.map(({ cashCUSIP }) => securitiesStore[cashCUSIP])],
          error: FailedResolutionErrorTypes.MULTIPLE_CURRENCIES,
        });
        currenciesInSecurityStore.forEach(({ cashCUSIP }) => {
          securityMap[cashCUSIP] = securitiesStore[cashCUSIP];
        });
      }
      // send inappropriate securities that exist in securityMap and fulfills secTypeaheadPreFilters to error handling process
    } else if (filters?.length && security && !filters.every((filter) => doesSecurityPassFilter(security, filter))) {
      failedResolutions.push({
        ...ui,
        message: 'Cannot recognize security',
        alternates: [],
        error: FailedResolutionErrorTypes.UNRECOGNIZED_POSITIONS,
      });
    } else if (matches.length > 1) {
      if (currencyOptions.length === 1) {
        ui.currency = currencyOptions[0];
      }
      failedResolutions.push({
        ...ui,
        message: `${ui.identifier} could be more than one security`,
        alternates: matches,
        shouldSkipCurrencySelection: currencyOptions.length === 1,
        error:
          currencyOptions.length === 1
            ? FailedResolutionErrorTypes.MULTIPLE_SECURITY_MAPPINGS
            : FailedResolutionErrorTypes.MULTIPLE_CURRENCIES,
      });
    } else if (matches.length === 0) {
      failedResolutions.push({
        ...ui,
        message: 'Cannot recognize security',
        alternates: matches,
        error: FailedResolutionErrorTypes.UNRECOGNIZED_POSITIONS,
      });
    }
    Object.assign(ui, { cusip: matches.length === 1 ? matches[0]?.cusip : null });
  });

  const positions: TPosition[] = identifiedPositions.map((pos) => {
    const uniqueKey = getPosIdentifierKey(pos);
    const internalCusip = uniqueIdentifiers[uniqueKey]?.cusip ? { cusip: uniqueIdentifiers[uniqueKey].cusip } : {};
    return {
      ...pos,
      ...internalCusip,
    };
  });
  return {
    positions,
    marketValue,
    totalWeight,
    securityMap,
    failedResolutions,
  };
}

function deriveWeightsFromMarketValue(
  positionsData: TPosition[],
  overrideWeights?: boolean,
  overrideMarketValue?: number
) {
  const totalMv = overrideMarketValue
    ? overrideMarketValue
    : sumBy(positionsData, (pos) => (pos.marketValue ? pos.marketValue : 0));
  let hasBadWeights = false;
  if (totalMv) {
    forEach(positionsData, (position) => {
      if (position?.marketValue) {
        const derivedWeight = (100 * position.marketValue) / totalMv;
        if (!overrideWeights && position.weight && position.weight !== derivedWeight) {
          hasBadWeights = true;
          return false;
        }
        Object.assign(position, { weight: derivedWeight });
      }
      // Need this because of ts no implicit return rule
      return undefined;
    });
  }
  if (hasBadWeights) {
    throw new Error('Specify weights OR market values, not both');
  }
  return {
    positions: positionsData,
    marketValue: totalMv,
  };
}

/**
 * Given CSV data, read the data
 * @param textData
 * @param fileHeaderMappings
 * @returns {{headers: string[], data: *}}
 */
export function readFromCsv(textData: string, fileHeaderMappings?: FileHeaderMappings) {
  let headers = ['ticker', 'weight'];
  const normalizeTextDelimiter = (text: string): string => {
    const newLinePattern = /[\r\n]+/g;
    const tabPattern = /  +/g;
    // Replace new line characters with \n and multiple spaces with \t.
    return text.replace(newLinePattern, '\n').replace(tabPattern, '\t');
  };
  const normalizedTextData = normalizeTextDelimiter(textData);
  const detectDelimeter = (text: string) => {
    const delimiterPattern = /[;,\t\n]+/;
    const match = delimiterPattern.exec(text);
    if (match) {
      return match[0];
    }
    return undefined;
  };
  let delimiter;
  if (detectDelimeter(normalizedTextData)) {
    delimiter = detectDelimeter(normalizedTextData);
  } else {
    return;
  }

  let parsedOutput = Papa.parse<string[]>(normalizedTextData, {
    delimiter,
    skipEmptyLines: true,
  });
  const knownAliases: { [key: string]: string } = {
    mktValueBase: 'marketValue',
    posDate: 'portfolioDate',
    portfolioId: 'portfolioName',
  };
  let parsedData = parsedOutput.data.map((row) => row.map((cell) => DOMPurify.sanitize(cell, { ALLOWED_TAGS: [] })));
  if (parsedData.length === 0) {
    throw new Error('Unable to parse data');
  }
  let firstRow = parsedData[0];
  // fallback if PapaParse is still not able to find the delimiter (happens for Excel files)
  if (firstRow.length < 2) {
    parsedData = map(parsedData, (element) => element[0].split(/\s+/));
    firstRow = parsedData[0]!;
  }
  // Check first row for headers
  if (every(firstRow, (cell: string) => isNaN(parseFloat(cell)))) {
    // found headers
    headers = map(parsedData.shift(), (header: string) => {
      const normalizedHeader = header.startsWith('custom.') ? header : camelCase(header);
      if (knownAliases[normalizedHeader]) {
        return knownAliases[normalizedHeader];
      }
      if (fileHeaderMappings) {
        const headerMappings = Object.entries(fileHeaderMappings);
        // Match standard headers and file input case-insensitively, ignoring whitespace differences.
        const trimmedHeader = header.trim().toLowerCase().replace(/\s+/g, '');
        const matchedHeaderMapping = headerMappings.find((mapping) =>
          mapping[1].some((alias) => alias.trim().toLowerCase().replace(/\s+/g, '') === trimmedHeader)
        );
        if (matchedHeaderMapping) {
          return matchedHeaderMapping[0];
        }
      }
      // Try fuzzy matching as fallback
      const fuzzyResult = matchHeader(header, fileHeaderMappings);
      if (fuzzyResult.match && fuzzyResult.confidence !== 'none') {
        return fuzzyResult.match;
      }
      return normalizedHeader;
    });
  }

  if (parsedData.length === 0) {
    throw new Error('No data was provided');
  }
  return {
    headers,
    data: parsedData,
  };
}

interface ImportPositionsParams {
  currencies: CurrencyConfiguration[];
  date: string;
  decimalSeparatorFormat?: DecimalSeparatorFormat;
  defaultPortfolioCurrency: string;
  enableDefaultPortfolioCurrency: boolean;
  enableMatchRegionToMarket?: boolean;
  fileHeaderMappings?: FileHeaderMappings;
  filters: ApiTypes.FilterDefinition[] | undefined;
  isIndexOnlyUpload?: boolean;
  pkg: string;
  primarySecurityIdentifier?: string;
  rawData: string;
  securitiesStore: SecuritiesStore;
}

export async function importPositions({
  rawData,
  currencies,
  securitiesStore,
  date,
  pkg,
  enableDefaultPortfolioCurrency,
  defaultPortfolioCurrency,
  filters,
  enableMatchRegionToMarket,
  fileHeaderMappings,
  isIndexOnlyUpload,
  primarySecurityIdentifier,
  decimalSeparatorFormat,
}: ImportPositionsParams): Promise<ImportedPortfolio> {
  const parsedData = readFromCsv(rawData, fileHeaderMappings);
  if (!parsedData) {
    throw new Error('Error reading file');
  }

  let positionsData;
  try {
    positionsData = await buildPositionsFromRows({
      parsedData,
      currencies,
      securitiesStore,
      date,
      pkg,
      enableDefaultPortfolioCurrency,
      defaultPortfolioCurrency,
      filters,
      enableMatchRegionToMarket,
      isIndexOnlyUpload,
      primarySecurityIdentifier,
      decimalSeparatorFormat,
    });
  } catch (e) {
    if (e) {
      console.trace(e); // eslint-disable-line no-console -- error tracing
      throw new Error('Error importing positions');
    }
  }
  if (!positionsData) {
    throw new Error('Error importing positions');
  }
  return positionsData;
}

function doesSecurityPassFilter(security: Security, filter: ApiTypes.FilterDefinition) {
  const securityProperty = get(security, filter.field.name);
  if (filter.categories?.includes(securityProperty)) {
    return !filter.negate;
  } else if (filter.range) {
    const { start, end } = filter.range;
    return securityProperty >= start && securityProperty <= end;
  }
  return true;
}
